/**
 * handleIndexedDBError — Safari/WebKit IndexedDB crash recovery.
 *
 * WebKit has a long-standing bug (Bug #197050, filed April 2019, still
 * unfixed as of March 2026) where IndexedDB connections become corrupted
 * after background/foreground cycles, memory pressure, or iOS updates.
 *
 * The ONLY reliable recovery is a full page reload (100% success rate in production).
 * This module detects known error patterns and reloads IMMEDIATELY — no loop
 * protection needed. v1 has run this way for years without issues.
 *
 * The only guard: don't reload when the page is hidden (background).
 * Reloading in background causes infinite loops from silent push wake-ups.
 *
 * Ported from v1: src/utils/error/errorUtils.ts (lines 125-192).
 *
 * @see dreambase/reference/safari-indexeddb-crashes.md
 * @see policies/never-do.md NEVER-03
 */

// ---------------------------------------------------------------------------
// localStorage keys
// ---------------------------------------------------------------------------

const RECOVERY_SCROLL_KEY = "totodo_recovery_scroll"
const RECOVERY_TIMESTAMP_KEY = "totodo_recovery_timestamp"
const RELOAD_LOG_KEY = "totodo_idb_crash_log"
const PENDING_RELOAD_KEY = "totodo_idb_pending_reload"
const PENDING_RELOAD_FRESH_MS = 60_000

// ---------------------------------------------------------------------------
// Known Safari/WebKit IDB crash error messages
// ---------------------------------------------------------------------------

const RELOAD_ERROR_MESSAGES = [
  "Attempt to open a cursor in database without an in-progress transaction",
  "Attempt to get records from database without an in-progress transaction",
  "Attempt to get a record from database without an in-progress transaction",
  "Attempt to clear an object store without an in-progress transaction",
  "Attempt to iterate a cursor that doesn't exist",
  "Connection to Indexed Database server lost.",
  "An internal error was encountered in the Indexed Database server",
  "Attempt to delete range from database without an in-progress transaction",
  "Cannot inject key into script value",
  // dexie-cloud-patch lock timeout — indicates IDB deadlock after background
  "Lock job",
]

/** Keyword fragments for partial matching (covers minor wording variations) */
const RELOAD_KEYWORDS = [
  "Indexed Database server lost",
  "in-progress transaction",
  "iterate a cursor that doesn't exist",
  "internal error was encountered in the Indexed Database server",
  // dexie-cloud-patch: lock timeout means sync is permanently stuck
  "timed out after",
  "releasing lock to prevent deadlock",
]

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/**
 * Returns true if the error message matches a known Safari IDB crash pattern.
 */
export function isIndexedDBCrashError(errorMessage: string): boolean {
  return (
    RELOAD_ERROR_MESSAGES.some((msg) => errorMessage.includes(msg)) ||
    RELOAD_KEYWORDS.some((kw) => errorMessage.includes(kw))
  )
}

// ---------------------------------------------------------------------------
// Core handler
// ---------------------------------------------------------------------------

/**
 * If the error matches a known Safari IDB crash, reload the page immediately.
 *
 * NEVER-03: Only reloads when `document.visibilityState !== "hidden"`.
 * Reloading in background causes infinite loops from silent push wake-ups.
 *
 * No loop protection — reload ALWAYS fixes the issue (proven in v1 production
 * over 2+ years). Multiple simultaneous errors may trigger this, but only the
 * first one matters — the reload cancels everything.
 *
 * Returns true if a reload was initiated.
 */
export function handleIndexedDBError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)

  if (!isIndexedDBCrashError(message)) return false

  // CRITICAL: Don't reload if app is in background
  if (
    typeof document !== "undefined" &&
    document.visibilityState === "hidden"
  ) {
    console.warn(
      "⚠️ IndexedDB crash in background — skipping reload:",
      message,
    )
    scheduleReloadWhenVisible(message)
    logCrash(message, false)
    return false
  }

  // Immediate reload — no loop protection needed
  console.warn("🔄 IndexedDB crash detected, saving state and reloading:", message)
  logCrash(message, true)
  saveRecoveryState()
  window.location.reload()
  return true
}

function scheduleReloadWhenVisible(message: string): void {
  try {
    localStorage.setItem(
      PENDING_RELOAD_KEY,
      JSON.stringify({ message, timestamp: Date.now() }),
    )
  } catch {
    // Best effort
  }

  const onVisible = () => {
    if (document.visibilityState !== "visible") return
    document.removeEventListener("visibilitychange", onVisible)
    try {
      localStorage.removeItem(PENDING_RELOAD_KEY)
    } catch {
      // Best effort
    }
    console.warn("🔄 IndexedDB crash pending reload — app visible again, reloading")
    saveRecoveryState()
    window.location.reload()
  }

  document.addEventListener("visibilitychange", onVisible)
}

function saveRecoveryState(): void {
  try {
    // Save scroll position for current route
    const main = document.querySelector("main")
    if (main) {
      localStorage.setItem(
        RECOVERY_SCROLL_KEY,
        JSON.stringify({
          pathname: window.location.pathname,
          scrollTop: main.scrollTop,
        }),
      )
    }
    // Route is already saved by useNavigationHistory → totodo_nav_last_url
    // Save timestamp so startup knows this is a crash-reload
    localStorage.setItem(RECOVERY_TIMESTAMP_KEY, String(Date.now()))
  } catch {
    // Best effort
  }
}

// ---------------------------------------------------------------------------
// Recovery detection — used by startup code
// ---------------------------------------------------------------------------

/** Max age (ms) for a recovery state to be considered fresh. */
const RECOVERY_FRESH_MS = 10_000

/**
 * Returns recovery state if we're coming from a recent IDB crash reload.
 * Automatically clears the state after reading (one-shot).
 */
export function consumeRecoveryState(): {
  pathname: string
  scrollTop: number
} | null {
  try {
    const ts = localStorage.getItem(RECOVERY_TIMESTAMP_KEY)
    if (!ts) return null

    const age = Date.now() - Number(ts)
    if (age > RECOVERY_FRESH_MS) {
      // Stale — clean up
      localStorage.removeItem(RECOVERY_TIMESTAMP_KEY)
      localStorage.removeItem(RECOVERY_SCROLL_KEY)
      return null
    }

    // Read scroll state
    const raw = localStorage.getItem(RECOVERY_SCROLL_KEY)
    const scroll = raw ? JSON.parse(raw) : null

    // Clear one-shot state
    localStorage.removeItem(RECOVERY_TIMESTAMP_KEY)
    localStorage.removeItem(RECOVERY_SCROLL_KEY)

    return scroll ?? null
  } catch {
    return null
  }
}

/**
 * Startup recovery for background IDB crashes.
 * If a pending-reload marker exists and is fresh, reload once when visible.
 */
export function recoverPendingIndexedDBReload(): boolean {
  try {
    const raw = localStorage.getItem(PENDING_RELOAD_KEY)
    if (!raw) return false

    const parsed = JSON.parse(raw) as { timestamp?: number }
    const age = Date.now() - Number(parsed.timestamp ?? 0)
    if (!Number.isFinite(age) || age > PENDING_RELOAD_FRESH_MS) {
      localStorage.removeItem(PENDING_RELOAD_KEY)
      return false
    }

    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return false
    }

    localStorage.removeItem(PENDING_RELOAD_KEY)
    console.warn("🔄 Recovering pending IndexedDB reload at startup")
    saveRecoveryState()
    window.location.reload()
    return true
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Logging (best-effort localStorage)
// ---------------------------------------------------------------------------

function logCrash(message: string, reloaded: boolean): void {
  try {
    const existing = JSON.parse(
      localStorage.getItem(RELOAD_LOG_KEY) ?? "[]",
    ) as unknown[]
    existing.push({
      message,
      reloaded,
      timestamp: new Date().toISOString(),
      visibilityState: document.visibilityState,
    })
    if (existing.length > 20) existing.splice(0, existing.length - 20)
    localStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(existing))
  } catch {
    // Best effort
  }
}
